/**
 * `W2-T04` — the account page's writes: the name providers see, and the address book.
 *
 * One route `action` for five forms, told apart by `intent` (the convention the layout's sign-out
 * already uses). What comes back names the form it belongs to, so a refusal shows beside the
 * control that caused it and nowhere else.
 */
import { type SavedAddressCreate } from '@marketplace/contracts';
import { ApiError, type ApiClient } from '../../shared/api.js';
import { readAddress, type AddressField } from '../../shared/address.js';

export type AccountIntent =
  'profile' | 'address-create' | 'address-update' | 'address-delete' | 'address-default';

export type AddressFormField = AddressField | 'label';

export interface AccountActionData {
  intent: AccountIntent;
  /** The entry the form was about, for the four that have one. */
  addressId?: string;
  /** Translation keys, by field. */
  errors?: Partial<Record<AddressFormField | 'displayName', string>>;
  /** A translation key for a refusal that is not about one field. */
  failure?: string;
  saved?: true;
}

const INTENTS: readonly AccountIntent[] = [
  'profile',
  'address-create',
  'address-update',
  'address-delete',
  'address-default',
];

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

/** `ClientProfileUpdateSchema`'s bounds, restated so the person is told before anything is sent. */
const NAME_MAX = 80;
const LABEL_MAX = 60;

function readBookAddress(form: FormData): {
  address: SavedAddressCreate;
  errors: Partial<Record<AddressFormField, string>>;
} {
  const { address, errors } = readAddress(form, 'account.addresses');
  const label = text(form, 'label');
  const all: Partial<Record<AddressFormField, string>> = { ...errors };
  if (label === '') all.label = 'account.addresses.error.label';
  else if (label.length > LABEL_MAX) all.label = 'account.addresses.error.tooLong';
  return { address: { ...address, label }, errors: all };
}

/**
 * The refusal a person can act on, or the generic one. By status: the book's only `409` is the cap
 * (the contract documents it as `CONFLICT`), and `404` is an entry that is no longer theirs to touch.
 */
function failureOf(error: unknown, generic: string): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return 'account.addresses.error.limit';
    if (error.status === 404) return 'account.addresses.error.gone';
  }
  return generic;
}

/** `null` for a form this page does not have — the caller answers with nothing. */
export async function runAccountAction(
  form: FormData,
  api: ApiClient,
): Promise<AccountActionData | null> {
  const intent = INTENTS.find((candidate) => candidate === form.get('intent'));
  if (intent === undefined) return null;

  if (intent === 'profile') {
    const displayName = text(form, 'displayName');
    if (displayName === '') {
      return { intent, errors: { displayName: 'account.profile.error.required' } };
    }
    if (displayName.length > NAME_MAX) {
      return { intent, errors: { displayName: 'account.profile.error.tooLong' } };
    }
    try {
      await api.updateMyClientProfile({ displayName });
    } catch {
      return { intent, failure: 'account.profile.error.failed' };
    }
    return { intent, saved: true };
  }

  if (intent === 'address-create') {
    const { address, errors } = readBookAddress(form);
    if (Object.keys(errors).length > 0) return { intent, errors };
    try {
      await api.createMyAddress(address);
    } catch (error) {
      return { intent, failure: failureOf(error, 'account.addresses.error.failed') };
    }
    return { intent, saved: true };
  }

  const addressId = text(form, 'addressId');

  try {
    if (intent === 'address-update') {
      const { address, errors } = readBookAddress(form);
      if (Object.keys(errors).length > 0) return { intent, addressId, errors };
      await api.updateMyAddress(addressId, address);
    } else if (intent === 'address-delete') {
      await api.deleteMyAddress(addressId);
    } else {
      await api.setMyDefaultAddress(addressId);
    }
  } catch (error) {
    return { intent, addressId, failure: failureOf(error, 'account.addresses.error.failed') };
  }
  return { intent, addressId, saved: true };
}
